import { useMemo } from "react";
import {
  insufficientFunds,
  maxBtcAllowed,
  maximumStableCoins,
  maxRequiredSat,
  maxSatsAllowed,
  maxXAUTRequired,
  minBtcRequired,
  minOneSatRequired,
  minRequiredSat,
  minStableCoinRequired,
  minThousandSatsRequired,
  minXAUTRequired,
  receive,
  sats,
  send,
  sendWithHighFee,
  usdc,
  usdt,
  XAUT,
} from "../components/messages";
import {
  displaySelectedCurrencyAmount,
  fetchPrecisedInteger,
  getCurrencyObj,
  maxAmountAllowedSats,
  maximumAmountForStableCoins,
  maximumAmountForXAUT,
  minAmountRequiredForStableCoins,
  minAmountRequiredForXAUT,
  satsToBtcAmount,
} from "../components/constants";

function useSendButtonCalculation({
  amount,
  currency,
  balance,
  minTransactionAmt,
  maxTransactionAmt,
  sendMaxAmount,
  showBtcAmount,
  networkFeeAmount,
  calculatedSpeedFees,
  isLnUrlMethodReceive,
  isLnUrlMethodSend,
  isEthereumMethod,
  isTronMethod,
  isSolanaMethod,
  isTonMethod,
  isLightningMethod,
  isOnchainMethod,
  setButtonColor,
}) {
  const sendCalculations = useMemo(() => {
    const numAmount = Number(amount);
    const numEstAmount = Number(networkFeeAmount);
    const numCalculatedSpeedFee = Number(calculatedSpeedFees);
    const insufficientAmount = numAmount > balance;
    let btnLabel = send,
      btnDisable = false,
      isInsufficientBalance = false;

    const numTotal = numAmount + numEstAmount + numCalculatedSpeedFee;

    // To handle min/max validations
    const validateLimits = (minAmount, maxAmount, minLabel, maxLabel) => {
      if (numAmount < minAmount) {
        btnLabel = minLabel;
        btnDisable = true;
      } else if (numAmount > maxAmount) {
        btnLabel = maxLabel;
        btnDisable = true;
      }
    };

    if (balance || balance === 0) {
      if (numTotal > balance && !isLnUrlMethodReceive) {
        btnLabel = insufficientFunds;
        btnDisable = true;
        isInsufficientBalance = true;
      } else if (isLnUrlMethodSend || isLnUrlMethodReceive) {
        const toFixedAmt = currency === sats ? (showBtcAmount ? 8 : 0) : 2;

        let minTxnAmt = 0,
          maxTxnAmt = 0;

        if (currency === sats) {
          minTxnAmt = Math.max(minTransactionAmt, 1);
          maxTxnAmt = Math.min(maxTransactionAmt, maxAmountAllowedSats);
          minTxnAmt = displaySelectedCurrencyAmount(minTxnAmt);
          maxTxnAmt = displaySelectedCurrencyAmount(maxTxnAmt);
        } else {
          minTxnAmt = Math.max(
            minTransactionAmt,
            minAmountRequiredForStableCoins
          );

          maxTxnAmt = Math.min(maxTransactionAmt, maximumAmountForStableCoins);
        }

        const maxAmtLabel = fetchPrecisedInteger(maxTxnAmt, toFixedAmt);
        const minAmtLabel = fetchPrecisedInteger(minTxnAmt, toFixedAmt);
        const currSymbol = (
          <>
            {
              getCurrencyObj(
                currency === sats && showBtcAmount ? "BTC" : currency
              ).symbol
            }
          </>
        );
        validateLimits(
          minTxnAmt,
          maxTxnAmt,
          minRequiredSat(minAmtLabel, currSymbol),
          maxRequiredSat(maxAmtLabel, currSymbol, isLnUrlMethodReceive)
        );

        if (!btnDisable) {
          btnLabel = isLnUrlMethodSend ? send : receive;
        }
      } else if (
        currency === usdt ||
        currency === usdc ||
        isEthereumMethod ||
        isTronMethod ||
        isSolanaMethod ||
        isTonMethod
      ) {
        validateLimits(
          currency === XAUT
            ? minAmountRequiredForXAUT
            : minAmountRequiredForStableCoins,
          currency === XAUT
            ? maximumAmountForXAUT
            : maximumAmountForStableCoins,
          currency === XAUT ? minXAUTRequired : minStableCoinRequired(currency),
          currency === XAUT ? maxXAUTRequired : maximumStableCoins(currency)
        );
        if (!btnDisable) {
          btnLabel = insufficientAmount ? insufficientFunds : send;
          btnDisable = insufficientAmount;
          isInsufficientBalance = insufficientAmount;
        }
      } else if (isLightningMethod) {
        if (showBtcAmount) {
          validateLimits(
            satsToBtcAmount(1),
            satsToBtcAmount(maxAmountAllowedSats),
            minBtcRequired(satsToBtcAmount(1)),
            maxBtcAllowed(satsToBtcAmount(maxAmountAllowedSats))
          );
        } else {
          validateLimits(
            1,
            maxAmountAllowedSats,
            minOneSatRequired(),
            maxSatsAllowed()
          );
        }
      } else if (isOnchainMethod) {
        const calculateFees = (numAmount * 9) / 100;
        if (showBtcAmount) {
          validateLimits(
            satsToBtcAmount(1000),
            satsToBtcAmount(maxAmountAllowedSats),
            minBtcRequired(satsToBtcAmount(1000)),
            maxBtcAllowed(satsToBtcAmount(maxAmountAllowedSats))
          );
        } else {
          validateLimits(
            1000,
            maxAmountAllowedSats,
            minThousandSatsRequired(),
            maxSatsAllowed()
          );
          if (!btnDisable && numEstAmount > calculateFees) {
            btnLabel = sendWithHighFee;
            setButtonColor("#FD371E");
          }
        }
      }
    }

    return {
      btnLabel,
      btnDisable,
      isInsufficientBalance,
    };
  }, [
    amount,
    networkFeeAmount,
    balance,
    sendMaxAmount,
    calculatedSpeedFees,
    currency,
  ]);

  return { sendCalculations };
}

export default useSendButtonCalculation;
